import React, { useEffect, useMemo, useState } from 'react';
import pb from '../../../lib/pocketbase';
import { useAdminData } from '../../../contexts/AdminDataContext';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import AppointmentForm from './AppointmentForm';
import frLocale from '@fullcalendar/core/locales/fr';

// Ensure FullCalendar CSS is loaded
const ensureFullCalendarCSS = () => {
    const urls = [
        'https://cdn.jsdelivr.net/npm/@fullcalendar/daygrid@6.1.15/main.min.css',
        'https://cdn.jsdelivr.net/npm/@fullcalendar/timegrid@6.1.15/main.min.css',
        'https://cdn.jsdelivr.net/npm/@fullcalendar/list@6.1.15/main.min.css',
    ];
    urls.forEach((href) => {
        if (!document.querySelector(`link[href="${href}"]`)) {
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = href;
            document.head.appendChild(link);
        }
    });
};

const typeOptions = [
    { value: 'call', label: 'Appel', color: '#64748b' },
    { value: 'visio', label: 'Visio', color: '#0ea5e9' },
    { value: 'rdv', label: 'RDV', color: '#8b5cf6' },
];

const statusOptions = [
    { value: 'proposed', label: 'Proposé', bg: '#f1f5f9', color: '#475569' },
    { value: 'accepted', label: 'Accepté', bg: '#dbeafe', color: '#1e40af' },
    { value: 'canceled', label: 'Annulé', bg: '#fef2f2', color: '#991b1b' },
    { value: 'scheduled', label: 'Planifié', bg: '#e0e7ff', color: '#3730a3' },
    { value: 'done', label: 'Terminé', bg: '#dcfce7', color: '#166534' },
];

function MeetingsManager({ strictAdmin = false, showUserFilter = false }) {
    useEffect(() => { try { ensureFullCalendarCSS(); } catch { } }, []);
    const { customers: ctxCustomers = [], projects: ctxProjects = [] } = useAdminData() || {};

    const [items, setItems] = useState([]);
    const [usersList, setUsersList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [openDrawer, setOpenDrawer] = useState(false);
    const [showQR, setShowQR] = useState(false);
    const [saving, setSaving] = useState(false);
    const [editing, setEditing] = useState(null);

    const [selectedDate, setSelectedDate] = useState(null);
    const [filters, setFilters] = useState({ search: '', type: '', status: '', customer_id: '', project_id: '', commercial_id: '' });

    // Load users for filter if needed
    // Load users for filter if needed
    useEffect(() => {
        if (showUserFilter) {
            pb.collection('users').getFullList({ sort: 'name', filter: 'role = "commercial"' }).then(res => setUsersList(res)).catch(() => { });
        }
    }, [showUserFilter]);

    const fetchMeetings = async () => {
        setLoading(true);
        setError('');
        try {
            // We fetch all recent meetings and filter client-side for "search"
            // Server-side filtering for strict fields
            const parts = [];
            if (filters.type) parts.push(`type = "${filters.type}"`);
            if (filters.status) parts.push(`status = "${filters.status}"`);
            if (filters.status) parts.push(`status = "${filters.status}"`);
            if (filters.customer_id) parts.push(`customer_id = "${filters.customer_id}"`);
            if (filters.project_id) parts.push(`project_id = "${filters.project_id}"`);

            // Logic for Billing vs Global
            if (strictAdmin) {
                // Show ONLY meetings with NO commercial_id (Admin/System)
                // Show ONLY meetings with NO commercial_id (Admin/System)
                parts.push('(commercial_id = "" || commercial_id = null || commercial_id.id = null)');
            } else if (filters.commercial_id) {
                // Global view: Filter by selected commercial if any
                parts.push(`commercial_id = "${filters.commercial_id}"`);
            }

            const filterStr = parts.length ? parts.join(' && ') : '';


            // Fetch generous amount for calendar
            const res = await pb.collection('meetings').getList(1, 500, { sort: '-start', filter: filterStr });
            setItems(res?.items ?? []);
        } catch (e) {
            setError(e?.message || 'Erreur au chargement');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchMeetings(); /* eslint-disable-next-line */ }, [filters.type, filters.status, filters.customer_id, filters.project_id, filters.commercial_id, strictAdmin]);

    const handleSave = async (payload) => {
        setSaving(true);
        try {
            if (editing?.id) {
                await pb.collection('meetings').update(editing.id, payload);
            } else {
                await pb.collection('meetings').create(payload);
            }
            setOpenDrawer(false);
            setEditing(null);
            await fetchMeetings();
        } catch (e) { alert('Erreur sauvegarde'); console.error(e); }
        finally { setSaving(false); }
    };

    const handleDelete = async () => {
        if (!editing?.id || !window.confirm('Supprimer ce RDV ?')) return;
        try {
            await pb.collection('meetings').delete(editing.id);
            setOpenDrawer(false);
            setEditing(null);
            await fetchMeetings();
        } catch (e) { alert('Erreur suppression'); }
    };

    // Names helpers
    const getCustomerName = (id) => ctxCustomers.find(c => c.id === id)?.name || '';
    const getProjectName = (id) => { const p = ctxProjects.find(i => i.id === id); return p?.name || p?.brand || ''; };

    // Filter Items by text search
    const filteredItems = useMemo(() => {
        if (!filters.search) return items;
        const s = filters.search.toLowerCase();
        return items.filter(m => {
            const cName = getCustomerName(m.customer_id).toLowerCase();
            const pName = getProjectName(m.project_id).toLowerCase();
            return (m.title || '').toLowerCase().includes(s) || cName.includes(s) || pName.includes(s);
        });
    }, [items, filters.search, ctxCustomers, ctxProjects]);

    // Map to FullCalendar events
    const events = useMemo(() => filteredItems.map(m => {
        const cust = getCustomerName(m.customer_id);
        const typ = typeOptions.find(t => t.value === m.type);
        const st = statusOptions.find(s => s.value === m.status);

        return {
            id: m.id,
            title: m.title || cust || 'RDV',
            start: m.start,
            end: m.end || m.start,
            backgroundColor: 'transparent',
            borderColor: 'transparent',
            extendedProps: { ...m, custName: cust },
            // Custom render props
            _typeColor: typ?.color || '#64748b',
            _statusBg: st?.bg || '#f1f5f9',
            _statusColor: st?.color || '#000',
        };
    }), [filteredItems, ctxCustomers]);

    // Calendar interactions
    const onDateSelect = (info) => {
        // Prefill start/end but without ID (so it counts as new)
        setEditing({
            start: info.startStr,
            end: info.endStr
        });
        setSelectedDate(info.startStr.slice(0, 10)); // YYYY-MM-DD
        setOpenDrawer(true);
    };

    const onEventClick = (info) => {
        setEditing({ id: info.event.id, ...info.event.extendedProps });
        setSelectedDate(null);
        setOpenDrawer(true);
    };

    const onEventDrop = async ({ event }) => {
        try {
            const startIso = event.start && !isNaN(event.start.getTime()) ? event.start.toISOString() : null;
            const endIso = event.end && !isNaN(event.end.getTime()) ? event.end.toISOString() : startIso;

            if (!startIso) return; // Prevent updating with invalid date

            await pb.collection('meetings').update(event.id, {
                start: startIso,
                end: endIso
            });
        } catch { fetchMeetings(); /* Revert on error */ }
    };

    // Custom Event Content
    const renderEventContent = (eventInfo) => {
        const p = eventInfo.event.extendedProps;
        const typeColor = eventInfo.event.extendedProps._typeColor || '#888';
        const stBg = eventInfo.event.extendedProps._statusBg;
        const stColor = eventInfo.event.extendedProps._statusColor;

        return (
            <div className="fc-event-content-custom">
                <div className="fc-event-line" style={{ borderLeft: `3px solid ${typeColor}` }}>
                    <div className="fc-time">{eventInfo.timeText}</div>
                    <div className="fc-title">{eventInfo.event.title}</div>
                    {p.custName && <div className="fc-subtitle">{p.custName}</div>}
                </div>
            </div>
        );
    };

    return (
        <div className="meetings-page-container">
            {/* Header / Toolbar */}
            <div className="meetings-toolbar">
                <div className="toolbar-left">
                    <div className="search-box">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
                        <input
                            type="text"
                            placeholder="Rechercher..."
                            value={filters.search}
                            onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
                        />
                    </div>
                </div>

                <div className="toolbar-right">
                    {showUserFilter && (
                        <select
                            className="filter-select"
                            value={filters.commercial_id}
                            onChange={e => setFilters(f => ({ ...f, commercial_id: e.target.value }))}
                        >
                            <option value="">Tous les utilisateurs (Global)</option>
                            {usersList.map(u => (
                                <option key={u.id} value={u.id}>{u.name || u.email}</option>
                            ))}
                        </select>
                    )}

                    <select
                        className="filter-select"
                        value={filters.status}
                        onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}
                    >
                        <option value="">Tous les statuts</option>
                        {statusOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>

                    <button
                        className="btn-new-meeting"
                        onClick={() => { setEditing(null); setSelectedDate(new Date().toISOString().split('T')[0]); setOpenDrawer(true); }}
                    >
                        + Nouveau RDV
                    </button>
                </div>
            </div>

            {/* Calendar Container */}
            <div className="calendar-wrapper card-clean">
                <FullCalendar
                    plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
                    initialView="timeGridWeek"
                    headerToolbar={{
                        left: 'prev,next today',
                        center: 'title',
                        right: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek'
                    }}
                    locale={frLocale}
                    editable={true}
                    selectable={true}
                    selectMirror={true}
                    dayMaxEvents={true}
                    weekends={true}
                    events={events}
                    select={onDateSelect}
                    eventClick={onEventClick}
                    eventDrop={onEventDrop}
                    eventResize={onEventDrop}
                    eventContent={renderEventContent}
                    height="auto"
                    slotMinTime="07:00:00"
                    slotMaxTime="20:00:00"
                    allDaySlot={false}
                    slotDuration="00:30:00"
                />
            </div>

            {/* Drawer (Side Panel) */}
            <div className={`drawer-overlay ${openDrawer ? 'open' : ''}`} onClick={() => setOpenDrawer(false)}>
                <div className="drawer-panel" onClick={e => e.stopPropagation()}>
                    <div className="drawer-header">
                        <h2>{editing?.id ? 'Modifier le Rendez-vous' : 'Nouveau Rendez-vous'}</h2>
                        <button className="btn-close" onClick={() => setOpenDrawer(false)}>✕</button>
                    </div>
                    <div className="drawer-body">
                        {/* We reuse AppointmentForm but style it to fit seamlessly */}
                        {openDrawer && (
                            <div className="form-wrapper">
                                <AppointmentForm
                                    appointment={editing}
                                    customers={ctxCustomers}
                                    projects={ctxProjects}
                                    onSubmit={handleSave}
                                    onCancel={() => setOpenDrawer(false)}
                                    loading={saving}
                                    selectedDate={selectedDate}
                                />
                                {editing?.id && (
                                    <div style={{ marginTop: 20, paddingTop: 20, borderTop: '1px solid #f1f5f9' }}>
                                        <button className="btn-delete" onClick={handleDelete}>Supprimer ce rendez-vous</button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {showQR && (
                <div className="modal-overlay" onMouseDown={(e) => { if (e.target.classList.contains('modal-overlay')) setShowQR(false); }}>
                    <div className="modal-content" style={{ maxWidth: 520, margin: 'auto', position: 'relative', top: '20%' }}>
                        <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', padding: 20, borderBottom: '1px solid var(--mt-border)' }}>
                            <h3 style={{ margin: 0 }}>QR code – Espace RDV</h3>
                            <button className="close-btn" onClick={() => setShowQR(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
                        </div>
                        <div className="modal-body" style={{ padding: 20, display: 'grid', gap: 12 }}>
                            {(() => {
                                const url = `${window.location.origin}/mon-compte/rendez-vous`; const qr = `https://api.qrserver.com/v1/create-qr-code/?size=240x240&margin=10&data=${encodeURIComponent(url)}`; return (
                                    <>
                                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                                            <img src={qr} alt="QR Espace RDV" width={240} height={240} style={{ borderRadius: 12, border: '1px solid var(--mt-border)', background: 'white' }} />
                                        </div>
                                        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
                                            <button className="header-pill header-shape" style={{ background: 'var(--mt-primary)', color: 'white', border: 'none' }} onClick={async () => { try { await navigator.clipboard.writeText(url); alert('Lien copié'); } catch { } }}>Copier le lien</button>
                                        </div>
                                    </>
                                );
                            })()}
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                /* Component Variables (SaaS Minimalist) */
                :root {
                   --mt-primary: #4f46e5; 
                   --mt-bg: #f8fafc;
                   --mt-card: #ffffff;
                   --mt-border: #e2e8f0;
                   --mt-text-main: #0f172a;
                   --mt-text-muted: #64748b;
                   --admin-accent: #4f46e5;
                }

                .meetings-page-container {
                    display: flex;
                    flex-direction: column;
                    gap: 16px;
                    font-family: 'Inter', sans-serif;
                    height: 100%;
                }

                /* Toolbar aligned with Quotes Page */
                .meetings-toolbar {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    flex-wrap: wrap;
                    gap: 12px;
                }
                .toolbar-left, .toolbar-right { display: flex; align-items: center; gap: 10px; }

                /* Header Pills (copied from Quotes) */
                .header-actions-frame{ display:flex; gap:8px; padding:0; border:none; background: transparent; }
                .header-pill{ cursor:pointer; background: transparent; border:1px solid transparent; color: var(--mt-text-main); padding:8px 10px; border-radius:10px; display: inline-flex; align-items: center; gap: 6px; font-size: 0.9rem; }
                .header-pill:hover{ background: rgba(0,0,0,0.04); border-color: var(--mt-border); }
                .header-shape{ border:1px solid var(--mt-border); background: var(--mt-card); }
                
                /* Tooltips */
                .has-tip{ position:relative; }
                .has-tip::after{ content: attr(data-tip); position:absolute; bottom: calc(100% + 8px); left:50%; transform: translateX(-50%); background: #1e293b; color: #fff; border-radius:6px; padding:6px 8px; font-size:.75rem; white-space:nowrap; opacity:0; pointer-events:none; transition: opacity .15s ease; z-index: 70; }
                .has-tip:hover::after{ opacity:1; }

                .search-box {
                    display: flex;
                    align-items: center;
                    background: var(--mt-card);
                    border: 1px solid var(--mt-border);
                    border-radius: 8px;
                    padding: 0 10px;
                    height: 40px;
                    width: 240px;
                    color: var(--mt-text-muted);
                    box-shadow: 0 1px 2px rgba(0,0,0,0.05);
                }
                .search-box input {
                    border: none;
                    background: transparent;
                    width: 100%;
                    outline: none;
                    margin-left: 8px;
                    color: var(--mt-text-main);
                    font-size: 0.9rem;
                }

                .filter-select {
                    height: 40px;
                    padding: 0 12px;
                    border: 1px solid var(--mt-border);
                    border-radius: 8px;
                    background: var(--mt-card);
                    color: var(--mt-text-main);
                    font-size: 0.9rem;
                    outline: none;
                    cursor: pointer;
                    box-shadow: 0 1px 2px rgba(0,0,0,0.05);
                }

                /* Btn New Meeting (Gradient style) */
                .btn-new-meeting {
                    display: inline-flex; 
                    align-items: center; 
                    gap: 10px;
                    padding: 10px 14px; 
                    border-radius: 10px;
                    background: linear-gradient(135deg, var(--admin-accent), rgba(79,70,229,0.85));
                    border: 1px solid rgba(79,70,229,0.6);
                    box-shadow: 0 6px 18px rgba(79,70,229,0.35);
                    color: white;
                    height: auto;
                    font-weight: normal;
                    font-size: 0.9rem;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }
                .btn-new-meeting:hover { transform: translateY(-1px); filter: brightness(1.04); }
                .btn-new-meeting:active { transform: translateY(0); filter: brightness(0.98); }

                /* Card container for Calendar */
                .card-clean {
                    background: var(--mt-card);
                    border: 1px solid var(--mt-border);
                    border-radius: 12px;
                    padding: 16px;
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.02), 0 2px 4px -1px rgba(0, 0, 0, 0.02);
                }

                /* FullCalendar Customizations - "Clean SaaS" Look */
                .fc { font-family: inherit; }
                .fc-toolbar-title { font-size: 1.25rem !important; font-weight: 600; color: var(--mt-text-main); }
                .fc-button {
                    background: transparent !important;
                    border: 1px solid var(--mt-border) !important;
                    color: var(--mt-text-main) !important;
                    font-weight: 500 !important;
                    text-transform: capitalize !important;
                    box-shadow: none !important;
                    padding: 6px 14px !important;
                }
                .fc-button:hover, .fc-button-active {
                    background: #f1f5f9 !important;
                    color: var(--mt-primary) !important;
                    border-color: #cbd5e1 !important;
                }
                .fc-col-header-cell-cushion { color: var(--mt-text-muted); font-weight: 600; padding: 10px 0 !important; text-decoration: none !important; }
                .fc-scrollgrid { border: none !important; }
                .fc-timegrid-slot { border-bottom: 1px dashed #f1f5f9 !important; }
                .fc-theme-standard td, .fc-theme-standard th { border-color: #f1f5f9; }

                /* Event Styling */
                .fc-event {
                    border: none !important;
                    background: transparent !important;
                    box-shadow: none !important;
                    margin: 1px 2px !important;
                }
                .fc-event-content-custom {
                    background: #f8fafc; 
                    border: 1px solid #e2e8f0;
                    border-radius: 6px;
                    padding: 4px;
                    height: 100%;
                    overflow: hidden;
                    box-shadow: 0 1px 2px rgba(0,0,0,0.03);
                    cursor: pointer;
                    transition: transform 0.1s, box-shadow 0.1s;
                }
                .fc-event-content-custom:hover {
                    box-shadow: 0 4px 6px rgba(0,0,0,0.05);
                    transform: translateY(-1px);
                    background: #ffffff;
                }
                .fc-event-line {
                    padding-left: 6px;
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    height: 100%;
                }
                .fc-time { font-size: 0.75rem; color: var(--mt-text-muted); font-weight: 500; }
                .fc-title { font-size: 0.85rem; color: var(--mt-text-main); font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
                .fc-subtitle { font-size: 0.75rem; color: var(--mt-text-muted); margin-top: 1px; }

                /* Drawer (Side Panel) replaced by Centered Modal */
                .drawer-overlay {
                    position: fixed; inset: 0;
                    background: rgba(0,0,0,0.5); /* Darker backdrop */
                    z-index: 2000;
                    opacity: 0; pointer-events: none;
                    transition: opacity 0.2s ease;
                    backdrop-filter: blur(2px);
                    display: flex; align-items: center; justify-content: center; /* Center content */
                }
                .drawer-overlay.open { opacity: 1; pointer-events: auto; }
                
                .drawer-panel {
                    position: relative; /* Not absolute full height */
                    width: 100%; max-width: 720px; /* Wider, like Quote modal */
                    background: var(--mt-card);
                    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
                    border-radius: 12px;
                    display: flex; flex-direction: column;
                    max-height: 90vh;
                    transform: scale(0.95);
                    transition: transform 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275);
                }
                .drawer-overlay.open .drawer-panel { transform: scale(1); }

                .drawer-header {
                    padding: 20px 24px;
                    border-bottom: 1px solid #e2e8f0;
                    display: flex; justify-content: space-between; align-items: center;
                }
                .drawer-header h2 { margin: 0; font-size: 1.25rem; color: var(--mt-text-main); }
                .btn-close { background: none; border: none; font-size: 1.25rem; cursor: pointer; color: var(--mt-text-muted); }
                .drawer-body { flex: 1; overflow-y: auto; padding: 24px; background: var(--mt-bg); border-radius: 0 0 12px 12px;}

                /* Common Modal (QR) */
                .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); z-index: 3000; }
                .modal-content { background: var(--mt-card); border-radius: 12px; height: auto; max-width: 500px; }

                /* Override AppointmentForm styles slightly inside drawer to fit Context */
                .form-wrapper .appointment-form-overlay { position: static; background: transparent; padding: 0; display: block; }
                .form-wrapper .appointment-form-container { width: 100%; max-width: none; box-shadow: none; border: none; max-height: none; }
                .form-wrapper .appointment-form-header { display: none; } /* Hide internal header since drawer has one */
                
                .btn-delete {
                    background: transparent; border: 1px solid #fee2e2; color: #b91c1c;
                    width: 100%; padding: 10px; border-radius: 8px; cursor: pointer;
                }
                .btn-delete:hover { background: #fef2f2; }

                /* Dark Mode Override Support */
                .dark {
                   --mt-primary: #6366f1;
                   --mt-bg: #0f172a;
                   --mt-card: #1e293b;
                   --mt-border: #334155;
                   --mt-text-main: #f8fafc;
                   --mt-text-muted: #94a3b8;
                   
                   /* Map variables for AppointmentForm to inherit */
                   --admin-panel: #1e293b;
                   --admin-panel-2: #020617; /* Darker input bg */
                   --admin-border: #334155;
                   --admin-text: #f8fafc;
                   --admin-muted: #94a3b8;
                   --admin-accent: #6366f1;
                }

                .dark .card-clean, .dark .search-box, .dark .filter-select, .dark .drawer-panel, .dark .header-shape {
                   background: var(--mt-card); border-color: var(--mt-border); color: var(--mt-text-main);
                }
                .dark .fc-button { color: #f1f5f9 !important; border-color: #334155 !important; }
                .dark .fc-toolbar-title { color: #f1f5f9; }
                .dark .fc-event-content-custom { background: #0f172a; border-color: #334155; }
                .dark .fc-title { color: #f1f5f9; }
                .dark .fc-event-content-custom:hover { background: #1e293b; }

                /* Hover effect on slots */
                .fc-timegrid-slot-lane:hover {
                    cursor: cell;
                    background-color: rgba(79, 70, 229, 0.08) !important; /* Slightly stronger highlight */
                    transition: background-color 0.1s;
                }
                /* Clean removal of pseudo-element to avoid positioning issues */
                .fc-timegrid-slot-lane:hover::after { content: none; }
                
                .fc-daygrid-day-frame:hover {
                     background-color: rgba(79, 70, 229, 0.05) !important;
                     cursor: cell;
                }
                /* Keep the Month view + as it worked well, or remove if user meant ALL views */
                .fc-daygrid-day-frame:hover::after {
                    content: "+";
                    position: absolute;
                    left: 50%;
                    top: 50%;
                    transform: translate(-50%, -50%);
                    color: var(--mt-primary);
                    font-weight: 800;
                    pointer-events: none;
                    font-size: 2rem;
                    opacity: 0.5;
                }
                .dark .fc-button:active, .dark .fc-button-active {
                   background: rgba(99, 102, 241, 0.2) !important;
                   color: var(--mt-primary) !important;
                   border-color: var(--mt-primary) !important;
                }
                .dark .fc-button:hover {
                   background: rgba(255, 255, 255, 0.1) !important;
                   color: #f1f5f9 !important;
                   border-color: var(--mt-border) !important;
                }

                /* Deep Dark Mode for FullCalendar Grid */
                .dark .fc-theme-standard th, 
                .dark .fc-theme-standard td, 
                .dark .fc-theme-standard .fc-scrollgrid {
                    border-color: var(--mt-border) !important;
                }
                
                .dark .fc-theme-standard th {
                    background-color: var(--mt-card) !important;
                }
                .dark .fc-col-header-cell-cushion,
                .dark .fc-timegrid-slot-label-cushion {
                    color: var(--mt-text-main) !important; /* Bright white text */
                    opacity: 0.9;
                }

                .dark .fc-day-today {
                    background: rgba(99, 102, 241, 0.1) !important;
                }

                /* List View Dark */
                .dark .fc-list { border-color: var(--mt-border); }
                .dark .fc-list-day-cushion { background: var(--mt-bg) !important; }
                .dark .fc-list-event:hover td { background: rgba(255,255,255,0.03) !important; }
                .dark .fc-list-day-text, .dark .fc-list-day-side-text { color: var(--mt-text-main) !important; }
                .dark .fc-list-event-title, .dark .fc-list-event-time { color: var(--mt-text-main) !important; }
                .dark .fc-list-event-dot { border-color: var(--mt-primary); }

                /* Hover effects on dark grid - Improved */
                .dark .fc-timegrid-slot-lane:hover { 
                    background-color: rgba(99, 102, 241, 0.15) !important; 
                    transition: background-color 0.2s ease;
                }
                .dark .fc-daygrid-day-frame:hover { 
                    background-color: rgba(99, 102, 241, 0.15) !important; 
                    transition: background-color 0.2s ease;
                }

            `}</style>
        </div>
    );
}

export default MeetingsManager;
